/**
 * What the build runner says about a failed build STEP, read out of the head of
 * its failure reason: which step failed, how much memory the build had, and the
 * Node heap ceiling it ran under.
 *
 * WHY THE REASON TEXT AND NOT A MARKER OR A COLUMN. The reason is the one
 * channel that already reaches every surface whole — both executors, both
 * report modes, every agent version, the build job, the deployment and the
 * audit row. A new stderr marker could not: an agent predating it stops its
 * bottom-up marker scan at the first line it does not know, and lifts the
 * reason by a byte count taken from right above that block, so any new line
 * there blinds every not-yet-upgraded agent to the code and the reason it
 * already reads. The head is written INSIDE the reason, so an old agent relays
 * it untouched and the card reads it wherever the reason went.
 *
 * WHY THIS IS SAFE TO READ. The verdict never comes from here — the code does
 * that (`VibeFailureReasonCode`). This only supplies specifics for a card to
 * quote, and only from the lines the RUNNER writes before any build output:
 *
 *   line 1  the head, which starts with one of the runner's literal prefixes
 *           below. The heap and OOM-killed heads carry no tenant text at all;
 *           the generic head quotes the builder's verdict, so nothing is read
 *           from it.
 *   line 2  `Failed step: <the step, as the builder named it>` — the runner's
 *           own line, written only below one of those heads and always above the
 *           step's output, which starts on the line after it.
 *
 * The runner keeps line 1 to one line (it flattens the builder's verdict), and
 * a reason the runner had to cut keeps line 1 and marks the cut on the line
 * below it, so no build output can ever land on line 2.
 *
 * The writer is `build-step.sh` in the builder image; a backend spec runs it and
 * reads its output back with this function, because no compiler connects them.
 * A reason from a runner predating the step line reads `step: null`, and one
 * predating the memory clause reads `memory: null`, so every older row still
 * renders — with fewer specifics, never with wrong ones.
 */

/** How the runner opens the reason of a build step that ran Node out of heap. */
export const VIBE_BUILD_HEAP_HEAD = "build: a build step ran out of JavaScript heap memory — ";

/** How the runner opens the reason of a build step the system killed for memory. */
export const VIBE_BUILD_OOM_KILLED_HEAD = "build: a build step was killed by the system";

/** How the runner opens the reason of any other failed build step. */
export const VIBE_BUILD_STEP_FAILED_HEAD = "build: a build step failed";

/** The runner's line naming the step that failed, directly under the head. */
export const VIBE_FAILED_STEP_LINE_PREFIX = "Failed step: ";

/**
 * Where the memory figure came from, in the runner's words: the envelope the
 * scheduler reserved for this build, or the whole build machine's memory.
 */
export type VibeBuildMemorySource = "reserved" | "host";

export interface VibeBuildStepFailure {
  /**
   * The step that failed, as the builder named it — BuildKit's
   * `[builder 4/5] RUN npm run build`, the legacy builder's
   * `Step 3/4 : RUN npm run build`. Null when the runner named none.
   */
  step: string | null;
  /** The memory the build had, in MiB, and where that figure came from. Null when unstated. */
  memory: { mib: number; source: VibeBuildMemorySource } | null;
  /** The Node heap ceiling the platform defaulted the build to, in MiB. Null when none was applied. */
  nodeHeapMiB: number | null;
}

const NOTHING: VibeBuildStepFailure = { step: null, memory: null, nodeHeapMiB: null };

/** The runner's memory clause — `4096 MiB reserved for this build`, `7805 MiB on the build host`. */
const MEMORY_CLAUSE = /\b([1-9][0-9]{0,6}) MiB (reserved for this build|on the build host)\b/;

/** The heap default the runner names — `--max-old-space-size=3072`. */
const HEAP_CLAUSE = /--max-old-space-size=([1-9][0-9]{0,6})\b/;

/**
 * Read a failed build step's specifics out of its reason. Total: anything that
 * is not one of the runner's step-failure heads reads as nothing named.
 */
export function readVibeBuildStepFailure(reason: string | null): VibeBuildStepFailure {
  if (reason === null) return NOTHING;
  const [head = "", second = ""] = reason.split("\n", 2);
  const stated = readHead(head);
  if (stated === null) return NOTHING;
  return { ...stated, step: readStepLine(second) };
}

/** The memory and heap a step-failure head states; null when `head` is no such head. */
function readHead(head: string): Omit<VibeBuildStepFailure, "step"> | null {
  if (head.startsWith(VIBE_BUILD_HEAP_HEAD)) {
    const heap = HEAP_CLAUSE.exec(head)?.[1];
    return { memory: readMemory(head), nodeHeapMiB: heap === undefined ? null : Number(heap) };
  }
  if (head.startsWith(VIBE_BUILD_OOM_KILLED_HEAD)) {
    return { memory: readMemory(head), nodeHeapMiB: null };
  }
  // The generic head quotes the builder's verdict — the tenant's own command —
  // so its line is never read for a figure.
  if (head.startsWith(VIBE_BUILD_STEP_FAILED_HEAD)) return { memory: null, nodeHeapMiB: null };
  return null;
}

function readMemory(head: string): VibeBuildStepFailure["memory"] {
  const match = MEMORY_CLAUSE.exec(head);
  if (match === null) return null;
  return {
    mib: Number(match[1]),
    source: match[2] === "reserved for this build" ? "reserved" : "host"
  };
}

function readStepLine(line: string): string | null {
  if (!line.startsWith(VIBE_FAILED_STEP_LINE_PREFIX)) return null;
  const step = line.slice(VIBE_FAILED_STEP_LINE_PREFIX.length).trim();
  return step === "" ? null : step;
}
